/** @jsx jsx */

/** @jsxFrag React.Fragment  */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { isRightToLeft } from 'js/lib/language';

import { Button, Typography2 as Typography, breakpoints, typography2 as typography } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';
import type { ButtonName, MerchandisingModule } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import ResponsiveImage from 'bundles/cds-labs/components/Modules/HeroBentoModule/ResponsiveImage';
import SignInModalButton from 'bundles/cds-labs/components/Modules/HeroBentoModule/SignInModalButton';
import {
  HERO_BENTO_CARD_DESKTOP_IMAGE_WIDTH,
  HERO_BENTO_CARD_TABLET_IMAGE_WIDTH,
  HERO_BENTO_MODULE_TABLET_BREAKPOINT,
} from 'bundles/cds-labs/components/Modules/HeroBentoModule/constants';
import Eyebrow, { type EyebrowProps } from 'bundles/cds-labs/components/Modules/components/Eyebrow';
import HoverEffectWrapper from 'bundles/cds-labs/components/Modules/components/HoverEffectWrapper';
import {
  TYPOGRAPHY_DARK_COLOR,
  TYPOGRAPHY_LIGHT_COLOR,
  getColorFromColorName,
  isInvertedColor,
  rotateLinearGradient,
  unifiedModuleColors,
} from 'bundles/cds-labs/components/Modules/utils/colors';

import _t from 'i18n!nls/cds-labs';

type CallToAction = {
  target: string;
  label: string;
  variant?: Extract<ButtonProps['variant'], 'primary' | 'secondary'>;
};

type ImageType = {
  url?: string;
  width?: number;
  height?: number;
  description?: string;
  sys?: { id?: string };
};

export type HeroBentoCardType = {
  id: string;
  index?: number | null;
  eyebrow?: EyebrowProps | null;
  headline?: string;
  description?: string;
  cta?: CallToAction | null;
  customPrimaryCta?: React.ReactNode;
  desktopImage?: ImageType | null;
  mobileImage?: ImageType | null;
  backgroundColor?: string | null;
  gradientBackgroundColor?: string | null;
  textColorOverride?: 'light' | 'dark' | 'auto' | null;
  campaignEventingData?: {
    id: string;
    matchedCountryCode: string;
    epicExperimentId: string;
    regionName: string;
    name: string;
  };
  isWiderCard?: boolean;
};

const SIGN_IN_MODAL = 'sign-in-modal';
const CARD_MOBILE_BREAKPOINT = breakpoints.values.sm - 50; // 50px buffer to account for padding/margin between cards in the carousel, so that there is a peek of the next card in HeroBentoModule before switching card layout from landscape to portrait

const styles = {
  container: css`
    height: 100%;
    container-type: inline-size;
    container-name: hero-bento-card;

    /* set position to relative to allow the CTA to overlay the entire card on hover */
    position: relative;
  `,
  card: css`
    height: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    border-radius: var(--cds-border-radius-300);
    overflow: hidden;

    @container hero-bento-card (width < ${CARD_MOBILE_BREAKPOINT}px) {
      grid-template-columns: none;
      justify-content: space-between;
      gap: var(--cds-spacing-200);
    }
  `,
  cardBorder: css`
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
  `,
  contentContainer: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding-left: var(--cds-spacing-200);
    gap: var(--cds-spacing-100);

    @container hero-bento-card (width < ${CARD_MOBILE_BREAKPOINT}px) {
      flex: unset;
      padding: calc(var(--cds-spacing-300) - var(--cds-spacing-50) / 2) var(--cds-spacing-200) 0;
    }
  `,
  title: css`
    font: var(--cds-typography-title-large-compact-sm);

    @container hero-bento-card (width < ${CARD_MOBILE_BREAKPOINT}px) {
      font: var(--cds-typography-title-x-small-sm);
      letter-spacing: var(--cds-letter-spacing-minus30);
    }
  `,
  description: css`
    ${typography.bodySecondary}

    @container hero-bento-card (width < ${CARD_MOBILE_BREAKPOINT}px) {
      display: none;
    }
  `,
  button: css`
    margin-bottom: var(--cds-spacing-200);

    &:hover {
      text-decoration: none;
    }

    @container hero-bento-card (width < ${CARD_MOBILE_BREAKPOINT}px) {
      .cds-button-endIcon {
        display: none;
      }
    }
  `,
  buttonOverlay: css`
    /* set position to static to allow the pseudo element below to overlay the entire card on hover */
    position: static !important;

    /* overlay entire card with the title link */
    &::before {
      content: '';
      cursor: inherit;
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      z-index: 0;
      width: 100%;
      height: 100%;
    }
  `,
  primaryButtonFocusStyle: css`
    &:focus-visible {
      text-decoration: none;
      outline: 1px solid transparent;
      outline-offset: 2px;
      box-shadow: 0 0 0 2px var(--cds-color-interactive-stroke-primary-focus-invert),
        0 0 0 3px var(--cds-color-interactive-primary);
    }
  `,
  secondaryButtonFocusStyle: css`
    &:focus-visible {
      text-decoration: none;
      outline: 1px solid transparent;
      outline-offset: 2px;
      box-shadow: 0 0 0 2px var(--cds-color-interactive-primary),
        0 0 0 3px var(--cds-color-interactive-stroke-primary-focus-invert);
    }
  `,
  imageContainer: css`
    display: flex;
    align-items: end;
    justify-content: end;

    img {
      max-height: 306px !important;
      width: 100%;
      object-fit: contain;
    }

    @container hero-bento-card (width < ${CARD_MOBILE_BREAKPOINT}px) {
      min-height: 138px;

      img {
        max-height: unset !important;
      }
    }
  `,
  mobileCtaContainer: css`
    display: none;

    @container hero-bento-card (width < ${CARD_MOBILE_BREAKPOINT}px) {
      display: block;
    }
  `,
  desktopCtaContainer: css`
    display: block;

    @container hero-bento-card (width < ${CARD_MOBILE_BREAKPOINT}px) {
      display: none;
    }
  `,
  gradientBackgroundStyle: (gradientBackgroundColor: string, rotatedGradient: string) => css`
    background-image: ${gradientBackgroundColor};
    background-position: top left;

    @container hero-bento-card (width < ${CARD_MOBILE_BREAKPOINT}px) {
      ${rotatedGradient ? `background-image: ${rotatedGradient};` : ''}
    }
  `,
};

const HeroBentoCard = ({
  isWiderCard = false,
  index = 0,
  eyebrow,
  headline,
  description,
  cta,
  customPrimaryCta,
  desktopImage,
  mobileImage,
  backgroundColor,
  gradientBackgroundColor,
  textColorOverride = 'auto',
  campaignEventingData,
}: HeroBentoCardType) => {
  const merchandisingModuleEventingData: MerchandisingModule = {
    moduleType: 'hero_module',
    index: index ?? 0,
    heading: headline,
    ...(campaignEventingData ? { campaign: campaignEventingData } : {}),
  };

  const track = useTracker();
  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_merchandising_module',
    {
      merchandisingModule: merchandisingModuleEventingData,
    },
    [],
    { fullyVisible: true }
  );

  const trackClick = (buttonName: ButtonName, linkName: string, linkTarget: string) => () => {
    track('click_merchandising_module', {
      merchandisingModule: merchandisingModuleEventingData,
      button: {
        name: buttonName,
        linkType: 'internal',
        linkName,
        linkURL: linkTarget,
      },
    });
  };

  if (!headline) return null;

  const isInverted = textColorOverride === 'light' || isInvertedColor(backgroundColor ?? '');
  let color = isInverted ? TYPOGRAPHY_LIGHT_COLOR : TYPOGRAPHY_DARK_COLOR;

  if (textColorOverride === 'dark') {
    color = TYPOGRAPHY_DARK_COLOR;
  }
  const backgroundColorStyle = {
    backgroundColor: getColorFromColorName(backgroundColor) ?? undefined,
    color,
  };

  const isLinearGradient = gradientBackgroundColor && gradientBackgroundColor.indexOf('linear-gradient') !== -1;
  const rotatedGradient =
    isLinearGradient && gradientBackgroundColor ? rotateLinearGradient(gradientBackgroundColor) : undefined;

  const backgroundGradientStyle = gradientBackgroundColor
    ? styles.gradientBackgroundStyle(gradientBackgroundColor, rotatedGradient ?? '')
    : undefined;

  const backgroundGradientRtlStyle = isRightToLeft(_t.getLocale())
    ? css`
        &::after {
          transform: scaleX(-1);
        }
      `
    : undefined;

  const ctaVariant = cta?.variant ?? (isInverted ? 'secondary' : 'primary');

  const buttonFocusStyle =
    cta?.variant === 'primary' ? styles.primaryButtonFocusStyle : styles.secondaryButtonFocusStyle;

  const hasEyebrow = eyebrow?.text || (eyebrow?.image && Object.values(eyebrow.image).some((val) => !!val));

  const renderButton = (buttonSize: ButtonProps['size']) => {
    if (!cta) return null;

    if (cta.target === SIGN_IN_MODAL) {
      return (
        <SignInModalButton
          label={cta.label}
          variant={ctaVariant}
          onBtnClick={trackClick('primary_cta', cta.label, cta.target)}
          css={[styles.buttonOverlay, buttonFocusStyle]}
          size={buttonSize}
        />
      );
    }

    return (
      <Button
        variant={ctaVariant}
        component="a"
        href={cta.target}
        size={buttonSize}
        iconPosition="after"
        icon={<ArrowNextIcon size="medium" />}
        onClick={trackClick('primary_cta', cta.label, cta.target)}
        css={[styles.button, styles.buttonOverlay, buttonFocusStyle]}
      >
        {cta.label}
      </Button>
    );
  };

  const renderCardCTA = () => {
    if (customPrimaryCta) return <div>{customPrimaryCta}</div>;

    if (!cta) return null;

    if (isWiderCard) {
      return (
        <>
          <div css={styles.desktopCtaContainer}>{renderButton('medium')}</div>
          <div css={styles.mobileCtaContainer}>{renderButton('small')}</div>
        </>
      );
    }

    return <div>{renderButton('small')}</div>;
  };

  const imageFetchPriority = typeof index === 'number' && index < 2 ? 'high' : 'auto';

  return (
    <div css={styles.container} ref={visibilityTrackingRef}>
      <HoverEffectWrapper
        css={[
          styles.card,
          backgroundColorStyle,
          backgroundGradientStyle,
          backgroundGradientRtlStyle,
          backgroundColor === unifiedModuleColors.white && styles.cardBorder,
        ]}
      >
        <div css={styles.contentContainer}>
          {hasEyebrow && (
            <Eyebrow
              text={eyebrow.text}
              background={eyebrow.background}
              image={eyebrow.image}
              eyebrowImageHeight={eyebrow.eyebrowImageHeight}
            />
          )}
          <Typography variant="titleLargeCompact" component="h2" color="inherit" css={styles.title}>
            {headline}
          </Typography>
          {description && (
            <Typography variant="bodySecondary" component="p" color="inherit" css={styles.description}>
              {description}
            </Typography>
          )}
          {renderCardCTA()}
        </div>
        <div css={styles.imageContainer}>
          <ResponsiveImage
            images={[
              {
                url: mobileImage?.url,
                id: mobileImage?.sys?.id,
                width: HERO_BENTO_CARD_TABLET_IMAGE_WIDTH,
                alt: mobileImage?.description,
                media: `(max-width: ${HERO_BENTO_MODULE_TABLET_BREAKPOINT}px)`,
              },
              {
                url: desktopImage?.url,
                id: desktopImage?.sys?.id,
                width: HERO_BENTO_CARD_DESKTOP_IMAGE_WIDTH,
                alt: desktopImage?.description,
                media: `(min-width: ${HERO_BENTO_MODULE_TABLET_BREAKPOINT + 1}px)`,
                isFallback: true,
              },
            ]}
            fetchPriority={imageFetchPriority}
          />
        </div>
      </HoverEffectWrapper>
    </div>
  );
};

export default HeroBentoCard;
